/*!
 *
 * detectIncognito v1.6.0
 *
 * https://github.com/Joe12387/detectIncognito
 *
 * MIT License
 *
 * Copyright (c) 2021 - 2025 Joe Rutkowski <Joe@dreggle.com>
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 *
 * Please keep this comment intact in order to properly abide by the MIT License.
 *
 **/
async function detectIncognito() {
  return await new Promise(function(resolve, reject) {
    let browserName = "Unknown";
    let callbackSettled = false;
    function __callback(isPrivate) {
      if (callbackSettled) {
        return;
      }
      callbackSettled = true;
      resolve({
        isPrivate,
        browserName
      });
    }
    function identifyChromium() {
      const ua = navigator.userAgent;
      if (ua.match(/Chrome/)) {
        if (navigator.brave !== void 0) {
          return "Brave";
        } else if (ua.match(/Edg/)) {
          return "Edge";
        } else if (ua.match(/OPR/)) {
          return "Opera";
        }
        return "Chrome";
      } else {
        return "Chromium";
      }
    }
    function feid() {
      let toFixedEngineID = 0;
      const neg = parseInt("-1");
      try {
        neg.toFixed(neg);
      } catch (e) {
        toFixedEngineID = e.message.length;
      }
      return toFixedEngineID;
    }
    function isSafari() {
      return feid() === 44 || feid() === 43;
    }
    function isChrome() {
      return feid() === 51;
    }
    function isFirefox() {
      return feid() === 25;
    }
    function isMSIE() {
      return navigator.msSaveBlob !== void 0;
    }
    async function currentSafariTest() {
      try {
        await navigator.storage.getDirectory();
        __callback(false);
      } catch (e) {
        const message = e instanceof Error && typeof e.message === "string" ? e.message : String(e);
        const matchesExpectedError = message.includes("unknown transient reason");
        __callback(matchesExpectedError);
      }
    }
    function safari13to18Test() {
      const tmp = String(Math.random());
      try {
        const dbReq = indexedDB.open(tmp, 1);
        dbReq.onupgradeneeded = (ev) => {
          const db = ev.target.result;
          const finish = (priv) => {
            __callback(priv);
          };
          try {
            db.createObjectStore("t", { autoIncrement: true }).put(new Blob());
            finish(false);
          } catch (err) {
            const message = err instanceof Error && typeof err.message === "string" ? err.message : String(err);
            if (message.includes("are not yet supported")) finish(true);
            else finish(false);
          } finally {
            db.close();
            indexedDB.deleteDatabase(tmp);
          }
        };
        dbReq.onerror = () => __callback(false);
      } catch {
        __callback(false);
      }
    }
    function oldSafariTest() {
      const openDB = window.openDatabase;
      const storage = window.localStorage;
      try {
        openDB(null, null, null, null);
      } catch (e) {
        __callback(true);
        return;
      }
      try {
        storage.setItem("test", "1");
        storage.removeItem("test");
      } catch (e) {
        __callback(true);
        return;
      }
      __callback(false);
    }
    async function safariPrivateTest() {
      var _a;
      if (typeof ((_a = navigator.storage) == null ? void 0 : _a.getDirectory) === "function") {
        await currentSafariTest();
      } else if (navigator.maxTouchPoints !== void 0) {
        safari13to18Test();
      } else {
        oldSafariTest();
      }
    }
    function getQuotaLimit() {
      var _a, _b;
      const w = window;
      return ((_b = (_a = w == null ? void 0 : w.performance) == null ? void 0 : _a.memory) == null ? void 0 : _b.jsHeapSizeLimit) ?? 1073741824;
    }
    function storageQuotaChromePrivateTest() {
      navigator.webkitTemporaryStorage.queryUsageAndQuota(
        function(_, quota) {
          const quotaInMib = Math.round(quota / (1024 * 1024));
          const quotaLimitInMib = Math.round(getQuotaLimit() / (1024 * 1024)) * 2;
          __callback(quotaInMib < quotaLimitInMib);
        },
        function(e) {
          reject(new Error("detectIncognito somehow failed to query storage quota: " + e.message));
        }
      );
    }
    function oldChromePrivateTest() {
      const fs = window.webkitRequestFileSystem;
      const success = function() {
        __callback(false);
      };
      const error = function() {
        __callback(true);
      };
      fs(0, 1, success, error);
    }
    function chromePrivateTest() {
      if (self.Promise !== void 0 && self.Promise.allSettled !== void 0) {
        storageQuotaChromePrivateTest();
      } else {
        oldChromePrivateTest();
      }
    }
    async function firefoxPrivateTest() {
      var _a;
      if (typeof ((_a = navigator.storage) == null ? void 0 : _a.getDirectory) === "function") {
        try {
          await navigator.storage.getDirectory();
          __callback(false);
        } catch (e) {
          const message = e instanceof Error && typeof e.message === "string" ? e.message : String(e);
          const matchesExpectedError = message.includes("Security error");
          __callback(matchesExpectedError);
          return;
        }
      }
      __callback(navigator.serviceWorker === void 0);
    }
    function msiePrivateTest() {
      __callback(window.indexedDB === void 0);
    }
    async function main() {
      if (isSafari()) {
        browserName = "Safari";
        await safariPrivateTest();
      } else if (isChrome()) {
        browserName = identifyChromium();
        chromePrivateTest();
      } else if (isFirefox()) {
        browserName = "Firefox";
        await firefoxPrivateTest();
      } else if (isMSIE()) {
        browserName = "Internet Explorer";
        msiePrivateTest();
      } else {
        reject(new Error("detectIncognito cannot determine the browser"));
      }
    }
    main().catch(reject);
  });
}
if (typeof window !== "undefined") {
  window.detectIncognito = detectIncognito;
}
export {
  detectIncognito as default,
  detectIncognito
};
